import React, { type ReactNode, type SelectHTMLAttributes } from 'react'
import { DropdownChevron, DropdownInputStyled } from './DropdownInput.styled'
import { SharedProps } from '../../Common/V2/Inputs/types'
import { Icon } from '@mobi/component-library/Common/V2'

type Props = SharedProps & {
  /**
   * A string or React node to use as the prefix.
   */
  prefix?: ReactNode
  /**
   * The placeholder text for the dropdown input.
   *
   * Only shows when the dropdown input is empty.
   */
  placeholder?: string
  /**
   * The value of the dropdown input.
   *
   * Setting this property will turn the dropdown input into a controlled input, doing so will require an `onChange` or `onInput` handler.
   * This property is mutually exclusive with `defaultValue`.
   */
  value?: string
  /**
   * The default value of the dropdown input.
   *
   * Setting this property will turn the dropdown input into an uncontrolled input.
   * This property is mutually exclusive with `value`.
   */
  defaultValue?: string
  /**
   * Whether the value of the dropdown input is invalid.
   */
  isInvalid?: boolean
} & Omit<SelectHTMLAttributes<HTMLSelectElement>, 'prefix' | 'disabled'>

//TODO: (MOBI-3133) base component is styled but dropdown menu/items needs to be styled
export const DropdownInput = ({
  id,
  name,
  className,
  label,
  helpText,
  prefix,
  isInvalid = false,
  isDisabled = false,
  children,
  placeholder,
  ...dropdownInputProps
}: Props) => {
  id ||= name

  prefix = typeof prefix === 'string' ? <span>{prefix}</span> : prefix

  return (
    <DropdownInputStyled
      className={className}
      invalid={isInvalid}
      disabled={isDisabled}
      isPlaceholderSelected={placeholder != null && dropdownInputProps.value === ''}
    >
      {label ? <label htmlFor={id}>{label}</label> : null}
      <div>
        {prefix}
        <select name={name} id={id} {...dropdownInputProps} disabled={isDisabled}>
          {placeholder ? (
            <option value='' hidden>
              {placeholder}
            </option>
          ) : null}
          {children}
        </select>
        <DropdownChevron>
          <Icon name='SolidChevronDown' size='1.6rem' />
        </DropdownChevron>
      </div>
      <span>{helpText}</span>
    </DropdownInputStyled>
  )
}

DropdownInput.displayName = 'DropdownInput'
